/**
 * Admin Customers Directory — platform-wide contact page.
 *
 * Replaces the 1,127-line inline Customers() component in App.js with
 * a clean sectioned redesign. Same data source (/admin/customers, now
 * enriched via customerMetricsService), better information hierarchy:
 *   - Six churn-tier tabs at top (All / Active / At-risk / Dormant /
 *     New / Guests) that filter server-side.
 *   - Summary strip with page-scoped counts + LTV total.
 *   - Sleek card grid with avatar / churn pill / LTV + tags footer.
 *   - Right-side flyout with real sections (Identity, LTV, Merchant,
 *     Recent orders, Refund lineage) — no more inline styles.
 *
 * CSS scoped with `cd-*` per CLAUDE.md (customers-directory).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, X, RefreshCw, Users, Key, Copy,
  ShoppingCart, DollarSign, Mail, Phone, MapPin, Clock,
  AlertCircle,
} from 'lucide-react';
import { useAccessControl } from '../App';           // exported below by App
import { useToast } from './ui/Toast';
import './CustomersDirectory.css';

const CHURN_META = {
  ALL:          { label: 'All',           color: '#6b7280' },
  ACTIVE:       { label: 'Active',        color: '#059669' },
  AT_RISK:      { label: 'At risk',       color: '#d97706' },
  DORMANT:      { label: 'Dormant',       color: '#6b7280' },
  NEW:          { label: 'New',           color: '#0891b2' },
  NEVER_BOUGHT: { label: 'Never bought',  color: '#dc2626' },
  UNKNOWN:      { label: 'Unknown',       color: '#9ca3af' },
};

function fmtNaira(v) {
  const n = Number(v) || 0;
  return `₦${n.toLocaleString('en-NG', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}
function fmtDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return '—'; }
}
function fmtDateTime(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return '—'; }
}
// Both endpoints that answer /admin/customers/:id shape merchant
// differently — adminCustomersPrisma nests store as { store: {name,slug} }
// and uses `merchantTier`; adminDataPrisma flattens store into
// `storeName`/`storeSlug` and uses `tier`. Normalize to one shape so
// the flyout doesn't care.
function normalizeMerchant(m) {
  if (!m) return null;
  return {
    id:            m.id,
    email:         m.email,
    merchantTier:  m.merchantTier || m.tier || null,
    store: m.store || (m.storeName || m.storeSlug
      ? { name: m.storeName, slug: m.storeSlug }
      : null),
  };
}

function fullName(c) {
  return [c?.firstName, c?.lastName].filter(Boolean).join(' ')
    || c?.email
    || 'Guest';
}
function initialsOf(c) {
  const name = fullName(c);
  return name.split(/\s+/).filter(Boolean).slice(0, 2)
    .map(w => w[0]?.toUpperCase() || '').join('') || '?';
}

function ChurnPill({ tier, size = 'md' }) {
  const m = CHURN_META[tier] || CHURN_META.UNKNOWN;
  return (
    <span className={`cd-pill cd-pill--${size}`} style={{ background: `${m.color}18`, color: m.color }}>
      <span className="cd-pill__dot" style={{ background: m.color }} />
      {m.label}
    </span>
  );
}

/* ══════════════════════════════════════════════════════════════════════ */
/*  Main page                                                              */
/* ══════════════════════════════════════════════════════════════════════ */

export default function CustomersDirectory() {
  const { apiWithAccessControl } = useAccessControl();
  const toast = useToast();
  const navigate = useNavigate();

  const [rows, setRows]         = useState([]);
  const [summary, setSummary]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 0 });

  // Tabs + filters
  const [tab, setTab] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');

  // Flyout state
  const [detailId, setDetailId] = useState(null);

  // Password reset modal (kept simple — same flow as before)
  const [pwModal, setPwModal] = useState(null);

  const load = useCallback(async (opts = {}) => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.set('page',  String(opts.page  || pagination.page));
      params.set('limit', String(opts.limit || pagination.limit));
      if (searchTerm.trim()) params.set('search', searchTerm.trim());
      if (tab === 'GUEST') {
        params.set('isGuest', 'true');
      } else if (tab !== 'ALL') {
        params.set('churnRisk', tab);
      }

      const res  = await apiWithAccessControl(`/admin/customers?${params.toString()}`);
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || `HTTP ${res.status}`);
      setRows(json.customers || []);
      setSummary(json.summary || null);
      setPagination((p) => ({ ...p, ...(json.pagination || {}) }));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiWithAccessControl, tab, searchTerm, pagination.page, pagination.limit]);

  // Reset to page 1 when tab or search changes
  useEffect(() => { load({ page: 1 }); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (searchTerm === '') return;
    const timer = setTimeout(() => load({ page: 1 }), 400);
    return () => clearTimeout(timer);
  }, [searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load({ page: 1 }); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const totalCount = pagination.total || 0;

  return (
    <div className="cd-page">
      <header className="cd-page__head">
        <div>
          <h1 className="cd-page__title">Contacts</h1>
          <p className="cd-page__subtitle">
            Platform-wide customer directory with lifetime value + churn signals.
            {totalCount > 0 && ` ${totalCount.toLocaleString('en-NG')} total.`}
          </p>
        </div>
        <button type="button" className="cd-btn cd-btn--ghost" onClick={() => load()} disabled={loading}>
          <RefreshCw size={14} /> {loading ? 'Loading…' : 'Refresh'}
        </button>
      </header>

      {/* Summary strip */}
      {summary && (
        <div className="cd-summary">
          <SummaryCard label="On this page" value={summary.pageSize.toLocaleString('en-NG')} />
          <SummaryCard label="LTV (page)" value={fmtNaira(summary.totalLTV)} tone="ok" />
          <SummaryCard label="Orders (page)" value={summary.totalOrders.toLocaleString('en-NG')} />
          <SummaryCard label="Guests (page)" value={summary.guestCount.toLocaleString('en-NG')} tone="muted" />
        </div>
      )}

      {/* Tabs (churn-tier filter) */}
      <nav className="cd-tabs" role="tablist">
        {[
          { key: 'ALL',          label: 'All' },
          { key: 'ACTIVE',       label: 'Active' },
          { key: 'AT_RISK',      label: 'At risk' },
          { key: 'DORMANT',      label: 'Dormant' },
          { key: 'NEW',          label: 'New' },
          { key: 'NEVER_BOUGHT', label: 'Never bought' },
          { key: 'GUEST',        label: 'Guests' },
        ].map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            className={`cd-tab ${tab === t.key ? 'cd-tab--active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
            {summary?.byChurn && t.key !== 'ALL' && t.key !== 'GUEST' && summary.byChurn[t.key] > 0 && (
              <span className="cd-tab__count">{summary.byChurn[t.key]}</span>
            )}
          </button>
        ))}
      </nav>

      {/* Search */}
      <div className="cd-searchbar">
        <Search size={16} />
        <input
          type="text"
          placeholder="Search by name, email, or phone…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && load({ page: 1 })}
        />
        {searchTerm && (
          <button type="button" className="cd-searchbar__clear" onClick={() => { setSearchTerm(''); load({ page: 1 }); }}>
            <X size={14} />
          </button>
        )}
      </div>

      {error && <div className="cd-error"><AlertCircle size={14} /> {error}</div>}

      {/* Card grid */}
      {loading && rows.length === 0 && (
        <div className="cd-empty">Loading customers…</div>
      )}
      {!loading && rows.length === 0 && (
        <div className="cd-empty">
          <Users size={40} />
          <h3>No contacts</h3>
          <p>Try a different tab or clear the search.</p>
        </div>
      )}
      {rows.length > 0 && (
        <div className="cd-grid">
          {rows.map((c) => (
            <ContactCard
              key={c.id}
              customer={c}
              onClick={() => setDetailId(c.id)}
              onResetPassword={async () => {
                if (!window.confirm(`Reset password for ${c.email}?`)) return;
                try {
                  const r = await apiWithAccessControl(`/admin/customers/${c.id}/reset-password`, { method: 'POST' });
                  const d = await r.json();
                  if (r.ok && d.success) {
                    setPwModal({ email: c.email, password: d.data.temporaryPassword });
                    toast.success('Password reset');
                  } else {
                    toast.error(d.error || 'Failed to reset password');
                  }
                } catch { toast.error('Network error'); }
              }}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="cd-pagination">
          <span>
            Showing {((pagination.page - 1) * pagination.limit) + 1}–
            {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total.toLocaleString('en-NG')}
          </span>
          <button
            type="button"
            className="cd-btn"
            disabled={pagination.page <= 1 || loading}
            onClick={() => { setPagination((p) => ({ ...p, page: p.page - 1 })); load({ page: pagination.page - 1 }); }}
          >Prev</button>
          <button
            type="button"
            className="cd-btn"
            disabled={pagination.page >= pagination.pages || loading}
            onClick={() => { setPagination((p) => ({ ...p, page: p.page + 1 })); load({ page: pagination.page + 1 }); }}
          >Next</button>
        </div>
      )}

      {/* Flyout */}
      {detailId && (
        <ContactFlyout
          customerId={detailId}
          apiWithAccessControl={apiWithAccessControl}
          onClose={() => setDetailId(null)}
          navigate={navigate}
        />
      )}

      {/* Password reset modal */}
      {pwModal && (
        <div className="cd-modal-scrim" onClick={() => setPwModal(null)}>
          <div className="cd-modal" onClick={(e) => e.stopPropagation()}>
            <h4>Temporary password for {pwModal.email}</h4>
            <p className="cd-modal__msg">Share this with the customer — it expires on first login.</p>
            <div className="cd-modal__pw">
              <code>{pwModal.password}</code>
              <button
                type="button"
                className="cd-btn cd-btn--primary"
                onClick={() => { navigator.clipboard.writeText(pwModal.password); toast.success('Copied'); }}
              >
                <Copy size={14} /> Copy
              </button>
            </div>
            <div className="cd-modal__actions">
              <button type="button" className="cd-btn" onClick={() => setPwModal(null)}>Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════ */
/*  Card                                                                   */
/* ══════════════════════════════════════════════════════════════════════ */

function ContactCard({ customer: c, onClick, onResetPassword }) {
  const m = c.metrics || {};
  const name = fullName(c);
  const storeName = c.merchant?.store?.name || null;
  return (
    <div className="cd-card" onClick={onClick} role="button" tabIndex={0}>
      <header className="cd-card__head">
        <span className="cd-avatar" aria-hidden="true">{initialsOf(c)}</span>
        <div className="cd-card__ident">
          <strong className="cd-card__name">{name}</strong>
          <span className="cd-card__email">{c.email || '—'}</span>
        </div>
        <ChurnPill tier={m.churnRisk} size="sm" />
      </header>

      <div className="cd-card__meta">
        {storeName && (
          <span className="cd-card__store" title={c.merchant?.email}>
            {storeName}
          </span>
        )}
        {c.isGuest && <span className="cd-card__chip cd-card__chip--muted">Guest</span>}
        {!c.isActive && <span className="cd-card__chip cd-card__chip--warn">Deactivated</span>}
      </div>

      <div className="cd-card__stats">
        <div className="cd-stat">
          <span className="cd-stat__value">{fmtNaira(m.totalSpent)}</span>
          <span className="cd-stat__label">Lifetime</span>
        </div>
        <div className="cd-stat">
          <span className="cd-stat__value">{(m.orderCount ?? 0).toLocaleString('en-NG')}</span>
          <span className="cd-stat__label">Orders</span>
        </div>
        <div className="cd-stat">
          <span className="cd-stat__value">{fmtNaira(m.avgOrderValue)}</span>
          <span className="cd-stat__label">Avg</span>
        </div>
      </div>

      <footer className="cd-card__foot">
        <span className="cd-card__foot-time">
          <Clock size={12} />
          {m.lastPurchaseAt
            ? `Last order ${m.daysSinceLastPurchase}d ago`
            : `Joined ${fmtDate(c.createdAt)}`}
        </span>
        <button
          type="button"
          className="cd-btn cd-btn--ghost cd-btn--sm"
          onClick={(e) => { e.stopPropagation(); onResetPassword(); }}
          title="Send reset password"
        >
          <Key size={12} />
        </button>
      </footer>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════ */
/*  Flyout                                                                 */
/* ══════════════════════════════════════════════════════════════════════ */

function ContactFlyout({ customerId, apiWithAccessControl, onClose, navigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoad] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoad(true);
      setError('');
      try {
        const res = await apiWithAccessControl(`/admin/customers/${customerId}`);
        const json = await res.json();
        if (!res.ok) throw new Error(json?.error || `HTTP ${res.status}`);
        if (!cancelled) {
          // Normalize two response shapes:
          //  - adminCustomersPrisma /customers/:id — flat: { ...customer, metrics, orders, merchant }
          //  - adminDataPrisma /customers/:customerId — wrapped: { success, data: { customer, metrics, recentOrders, merchant } }
          // Route collision picks whichever mounted first, so tolerate both.
          const payload = json?.data && json.data.customer
            ? {
                ...json.data.customer,
                metrics:  json.data.metrics,
                orders:   json.data.recentOrders,
                merchant: normalizeMerchant(json.data.merchant),
              }
            : { ...json, merchant: normalizeMerchant(json.merchant) };
          setData(payload);
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoad(false);
      }
    })();
    return () => { cancelled = true; };
  }, [apiWithAccessControl, customerId]);

  const c = data;
  const m = data?.metrics;

  return (
    <div className="cd-flyout-scrim" onClick={onClose}>
      <aside className="cd-flyout" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <header className="cd-flyout__head">
          {c && (
            <div className="cd-flyout__head-ident">
              <span className="cd-avatar cd-avatar--lg" aria-hidden="true">{initialsOf(c)}</span>
              <div>
                <h2>{fullName(c)}</h2>
                <span className="cd-flyout__head-sub">
                  {c.email || '—'}
                  {c.isGuest && <span className="cd-card__chip cd-card__chip--muted" style={{ marginLeft: 8 }}>Guest</span>}
                </span>
              </div>
            </div>
          )}
          <button type="button" className="cd-btn cd-btn--ghost" onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </header>

        <div className="cd-flyout__body">
          {loading && <div className="cd-loading">Loading…</div>}
          {error && <div className="cd-error"><AlertCircle size={14} /> {error}</div>}
          {c && m && (
            <>
              {/* Lifetime value */}
              <section className="cd-block">
                <h3 className="cd-block__title">Lifetime</h3>
                <div className="cd-block__row"><span>Churn tier</span><strong><ChurnPill tier={m.churnRisk} size="sm" /></strong></div>
                <div className="cd-block__row"><span>Total spent</span><strong>{fmtNaira(m.totalSpent)}</strong></div>
                <div className="cd-block__row"><span>Orders</span><strong>{m.orderCount}</strong></div>
                <div className="cd-block__row"><span>Avg order value</span><strong>{fmtNaira(m.avgOrderValue)}</strong></div>
                <div className="cd-block__row"><span>First purchase</span><strong>{fmtDate(m.firstPurchaseAt)}</strong></div>
                <div className="cd-block__row">
                  <span>Last purchase</span>
                  <strong>
                    {fmtDate(m.lastPurchaseAt)}
                    {m.daysSinceLastPurchase != null && (
                      <span className="cd-block__muted"> · {m.daysSinceLastPurchase}d ago</span>
                    )}
                  </strong>
                </div>
                {c.refundsCount > 0 && (
                  <>
                    <div className="cd-block__row"><span>Refunds</span><strong>{c.refundsCount} ({c.refundRate}%)</strong></div>
                    <div className="cd-block__row"><span>Refunded amount</span><strong>{fmtNaira(c.totalRefunded)}</strong></div>
                    <div className="cd-block__row"><span>Net spent</span><strong>{fmtNaira(c.netSpent)}</strong></div>
                  </>
                )}
              </section>

              {/* Identity */}
              <section className="cd-block">
                <h3 className="cd-block__title">Contact</h3>
                <div className="cd-block__row"><span><Mail size={12} /> Email</span><strong>{c.email || '—'}</strong></div>
                <div className="cd-block__row"><span><Phone size={12} /> Phone</span><strong>{c.phone || '—'}</strong></div>
                <div className="cd-block__row"><span>Email verified</span><strong>{c.isEmailVerified ? 'Yes' : 'No'}</strong></div>
                <div className="cd-block__row"><span>Phone verified</span><strong>{c.isPhoneVerified ? 'Yes' : 'No'}</strong></div>
                <div className="cd-block__row"><span>Newsletter</span><strong>{c.newsletterSubscription ? 'Subscribed' : 'Off'}</strong></div>
                <div className="cd-block__row"><span>Signed up</span><strong>{fmtDateTime(c.createdAt)}</strong></div>
                <div className="cd-block__row"><span>Last login</span><strong>{fmtDateTime(c.lastLogin)}</strong></div>
              </section>

              {/* Tags + groups */}
              {(c.tags?.length > 0 || c.groups?.length > 0) && (
                <section className="cd-block">
                  <h3 className="cd-block__title">Segments</h3>
                  {c.tags?.length > 0 && (
                    <div className="cd-tag-line">
                      <span className="cd-tag-line__label">Tags</span>
                      <div className="cd-tags">
                        {c.tags.map((t) => <span key={t} className="cd-chip">{t}</span>)}
                      </div>
                    </div>
                  )}
                  {c.groups?.length > 0 && (
                    <div className="cd-tag-line">
                      <span className="cd-tag-line__label">Groups</span>
                      <div className="cd-tags">
                        {c.groups.map((g) => <span key={g} className="cd-chip cd-chip--group">{g}</span>)}
                      </div>
                    </div>
                  )}
                </section>
              )}

              {/* Addresses */}
              {c.addresses?.length > 0 && (
                <section className="cd-block">
                  <h3 className="cd-block__title">Addresses ({c.addresses.length})</h3>
                  {c.addresses.slice(0, 5).map((a) => (
                    <div key={a.id} className="cd-address">
                      <MapPin size={12} />
                      <span>{[a.line1, a.city, a.state, a.country].filter(Boolean).join(', ')}</span>
                    </div>
                  ))}
                </section>
              )}

              {/* Recent orders */}
              <section className="cd-block">
                <h3 className="cd-block__title">Recent orders ({c.orders?.length || 0})</h3>
                {(!c.orders || c.orders.length === 0) && <p className="cd-block__empty">No orders.</p>}
                {c.orders?.map((o) => (
                  <div key={o.id} className="cd-order">
                    <div>
                      <ShoppingCart size={12} />
                      <span className="cd-order__id">{o.id.slice(0, 8)}</span>
                      <span className="cd-block__muted"> · {o.status}</span>
                    </div>
                    <div className="cd-order__amt">
                      <strong>{fmtNaira(o.total)}</strong>
                      <span className="cd-block__muted">{fmtDate(o.createdAt)}</span>
                    </div>
                  </div>
                ))}
              </section>

              {/* Merchant */}
              {c.merchant && (
                <section className="cd-block">
                  <h3 className="cd-block__title">Merchant</h3>
                  <div className="cd-block__row"><span>Store</span><strong>{c.merchant.store?.name || '—'}{c.merchant.store?.slug ? ` (/${c.merchant.store.slug})` : ''}</strong></div>
                  <div className="cd-block__row"><span>Tier</span><strong>{c.merchant.merchantTier || 'FREE'}</strong></div>
                  <div className="cd-block__row"><span>Email</span><strong>{c.merchant.email}</strong></div>
                  <div className="cd-flyout__actions">
                    <button
                      type="button"
                      className="cd-btn cd-btn--primary"
                      onClick={() => navigate(`/merchants/${c.merchant.id}/customers`)}
                    >
                      All customers for this merchant
                    </button>
                    <button
                      type="button"
                      className="cd-btn"
                      onClick={() => navigate(`/merchants/${c.merchant.id}/orders`)}
                    >
                      Merchant orders
                    </button>
                  </div>
                </section>
              )}
            </>
          )}
        </div>
      </aside>
    </div>
  );
}

function SummaryCard({ label, value, tone }) {
  return (
    <div className={`cd-sumcard ${tone ? `cd-sumcard--${tone}` : ''}`}>
      <div className="cd-sumcard__label">{label}</div>
      <div className="cd-sumcard__value">{value}</div>
    </div>
  );
}
